import { Spin as Loader } from 'antd';
import React from 'react';
import Scroll from 'react-infinite-scroller';
import { FormattedMessage } from 'react-intl';
import { useDebouncedCallback } from 'use-debounce';

import Alert from './Alert';

const DEBOUNCED_TIME = 100;
const THRESHOLD_DEFAULT = 150;

/**
 * @typedef {{
 *   loadMore: () => void,
 *   hasMore?: unknown,
 *   apiError?: unknown,
 *   isFetching?: boolean,
 *   className?: string,
 *   useParent?: boolean,
 *   threshold?: number,
 *   children?: React.ReactNode,
 * } & Record<string, unknown>} InfiniteScrollProps
 */

/** @type {React.FC<InfiniteScrollProps>} */
const InfiniteScroll = ({
  apiError,
  hasMore,
  isFetching,
  loadMore,
  children,
  className,
  useParent,
  threshold = THRESHOLD_DEFAULT,
  ...rest
}) => {
  const [scrollNode, setScrollNode] = React.useState(null);
  const scrollRef = React.useCallback(node => {
    setScrollNode(node);
  }, []);

  const hasMoreItems = Boolean(!apiError && hasMore);

  const handleLoadMore = React.useCallback(() => {
    if (!isFetching && hasMoreItems) {
      loadMore();
    }
  }, [isFetching, hasMoreItems, loadMore]);

  const debouncedCallback = useDebouncedCallback(({ scrollHeight, clientHeight }) => {
    if (Boolean(scrollHeight) && scrollHeight <= clientHeight && hasMoreItems) {
      handleLoadMore();
    }
  }, DEBOUNCED_TIME);

  const target = React.useMemo(
    () => (useParent ? scrollNode?.parentNode : document.getElementById('main-content')),
    [useParent, scrollNode],
  );

  // This is only usefull for the Scroll component
  const getScrollParent = React.useCallback(() => target, [target]);

  // Handle loadMore when resizing screen
  React.useLayoutEffect(() => {
    const resizeObserver = new ResizeObserver(() => debouncedCallback(target));

    if (target) {
      resizeObserver.observe(target);
    }

    return () => resizeObserver.disconnect();
  }, [hasMoreItems, debouncedCallback, target]);

  // Handle loadMore when initial scrollHeight is <= than clientHeight
  React.useLayoutEffect(() => {
    if (target) {
      const { scrollHeight, clientHeight } = target;
      // BMWVID-17956 - Prevent 'loadMore' call when a component gonna be fold
      const isSmaller = Boolean(scrollHeight) && scrollHeight - threshold <= clientHeight;
      if (isSmaller) {
        handleLoadMore();
      }
    }
  }, [target, threshold, handleLoadMore]);

  return (
    <div ref={scrollRef}>
      <Scroll
        {...rest}
        useWindow={false}
        initialLoad={false}
        threshold={threshold}
        className={className}
        hasMore={hasMoreItems}
        loadMore={handleLoadMore}
        getScrollParent={getScrollParent}
      >
        {children}
        {isFetching && <Loader className="d-flex justify-content-center mt-2" />}
        {apiError && (
          <Alert
            type="error"
            role="infinite-scroll-alert"
            title={<FormattedMessage id="http.error.message" defaultMessage="Oops! Something went wrong." />}
          />
        )}
      </Scroll>
    </div>
  );
};

export default InfiniteScroll;
